import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/services/api';
import DiagnosticsView from '@/components/DiagnosticsView';
import StatusBar from '@/components/Jarvis/StatusBar';

/**
 * Mission Control — live window into JARVIS's mind.
 *
 * Four columns:
 *   1. Current thought + stream of consciousness scrolling live
 *   2. World state header + vital signs (health, weather, location)
 *   3. Horizon milestones + pending action queue
 *   4. System status (provider, discovery cycles, world model confidence)
 *
 * Polls /jarvis/mind/stream every 15s for new thoughts. No WebSocket
 * dependency for now — rotate to SSE later if needed.
 */

interface Thought {
  id: string;
  created_at: string;
  domain: string;
  kind: string;
  summary?: string | null;
  content: string;
  confidence?: number;
  priority?: number;
  sources?: string[];
  action_status?: string;
  action_type?: string | null;
}

interface Milestone {
  id: string;
  title: string;
  category: string;
  days_until: number | null;
  priority?: number;
}

interface InvestigationStep {
  sim_id: string;
  question: string;
  findings: string[];
  at: number;
}

interface Investigation {
  id: string;
  domain: string;
  title?: string | null;          // Haiku-summarized 5-8 word headline
  seed_question: string;
  status: string;
  steps: InvestigationStep[];
  converged_finding: string | null;
  created_at: number;
  updated_at: number;
}

const DOMAIN_COLOR: Record<string, string> = {
  iron_herald: '#ff8800',
  health: '#ff4466',
  life_patterns: '#00d4ff',
  relationships: '#a855f7',
  work: '#ffd700',
  music: '#66ff99',
  world: '#8899ff',
  meta: '#cccccc',
  general: '#aaaaaa',
};

function ageString(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const m = Math.max(0, Math.floor((now - then) / 60000));
  if (m < 1) return 'now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function MissionControlPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const tab: 'mind' | 'diagnostics' = location.pathname.endsWith('/diagnostics')
    ? 'diagnostics'
    : 'mind';

  const switchTab = (next: 'mind' | 'diagnostics') => {
    if (next === 'diagnostics') {
      navigate('/mission/diagnostics');
    } else {
      navigate('/mission');
    }
  };

  const [thoughts, setThoughts] = useState<Thought[]>([]);
  const [pending, setPending] = useState<Thought[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [greeting, setGreeting] = useState<{ greeting?: string; thought?: string | null } | null>(null);
  const [health, setHealth] = useState<{ hr?: number | null; steps?: number | null; sleep?: number | null }>({});
  const [worldConfidence, setWorldConfidence] = useState<number | null>(null);
  const [provider, setProvider] = useState<string>('—');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [expandedInv, setExpandedInv] = useState<Record<string, boolean>>({});
  // Sprint 14-A A.4 — cross-service API usage panel.
  const [usage, setUsage] = useState<ApiUsageSummary | null>(null);

  useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const [s, p, g, h, hs, inv, u] = await Promise.all([
          api.get<any>('/jarvis/mind/stream', { limit: 20 }),
          api.get<any>('/jarvis/mind/pending').catch(() => ({})),
          api.get<any>('/jarvis/mind/latest').catch(() => ({})),
          api.get<any>('/horizon/milestones', { days_ahead: 90 }).catch(() => ({})),
          api.get<any>('/health/summary').catch(() => ({})),
          api.get<any>('/jarvis/mind/investigations', { limit: 10 }).catch(() => []),
          // Sprint 14-A A.4 — cross-service usage + Max dispatch headline.
          api.get<ApiUsageSummary>('/admin/api_usage', { window_hours: 24 }).catch(() => null),
        ]);
        if (!mounted) return;

        setThoughts(s.items || s.thoughts || []);
        setWorldConfidence(typeof s.world_confidence === 'number' ? s.world_confidence : null);

        setPending(p.items || p.thoughts || []);

        setGreeting({
          greeting: g.greeting || undefined,
          thought: g.thought || g.thought_summary || null,
        });

        setMilestones(h.items || h.milestones || []);
        setInvestigations(Array.isArray(inv) ? inv : []);

        setHealth({
          hr: hs?.heart_rate?.value ?? null,
          steps: hs?.steps?.total ?? null,
          sleep: hs?.sleep?.total_hours ?? null,
        });

        setUsage(u ?? null);

        setLastRefresh(new Date());
      } catch (err) {
        console.error('[MissionControl] refresh failed', err);
      }
    };
    refresh();
    const id = setInterval(refresh, 15000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const t = localStorage.getItem('jarvis_provider') || 'CLAUDE';
    setProvider(t);
  }, []);

  const primaryThought = useMemo(() => thoughts[0] ?? null, [thoughts]);

  // Tab key swaps between MIND and DIAGNOSTICS. Carter asked for it
  // explicitly so the two views feel like the same screen, not two
  // pages. Ignored when a focusable element is in front of the user
  // (input/textarea/select) so it doesn't hijack form fields.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const t = e.target as HTMLElement | null;
      const tag = (t?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      e.preventDefault();
      switchTab(tab === 'mind' ? 'diagnostics' : 'mind');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tab]);

  const subtitle = tab === 'diagnostics' ? 'MISSION · DIAGNOSTICS' : 'MISSION · MIND';
  const rightExtras = (
    <div className="flex items-center gap-3 text-[9px] font-mono tracking-wider text-gray-500">
      <span>
        WM <span className="text-jarvis-blue/80 tabular-nums">{worldConfidence != null ? `${worldConfidence}%` : '—'}</span>
      </span>
      <span>
        SYNC <span className="text-jarvis-blue/80">{lastRefresh ? ageString(lastRefresh.toISOString()) : '—'}</span>
      </span>
    </div>
  );

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-jarvis-deep-dark text-white font-mono">
      {/* Floating top capsules (matches main page exactly). */}
      <StatusBar
        subtitle={subtitle}
        rightExtras={rightExtras}
        homeOnLogoClick
      />

      {/* Slim tab strip below the capsules — keeps the rest of the
          screen focused on content, not chrome. Tab key alternates. */}
      <div className="pt-16 px-6 pb-2 flex items-center gap-3">
        <div className="flex items-center border border-jarvis-blue/20">
          <button
            onClick={() => switchTab('mind')}
            className={`text-[10px] tracking-widest px-3 py-1 transition ${
              tab === 'mind'
                ? 'bg-jarvis-blue/20 text-jarvis-blue'
                : 'text-jarvis-blue/50 hover:text-jarvis-blue/80'
            }`}
          >
            MIND
          </button>
          <button
            onClick={() => switchTab('diagnostics')}
            className={`text-[10px] tracking-widest px-3 py-1 transition ${
              tab === 'diagnostics'
                ? 'bg-jarvis-blue/20 text-jarvis-blue'
                : 'text-jarvis-blue/50 hover:text-jarvis-blue/80'
            }`}
          >
            DIAGNOSTICS
          </button>
        </div>
        <span className="text-[9px] tracking-widest text-gray-600">
          PROVIDER · <span className="text-jarvis-blue/70">{provider}</span>
        </span>
        <span className="text-[9px] tracking-widest text-gray-600 ml-auto">
          ⇥ to swap views
        </span>
      </div>

      {tab === 'diagnostics' ? (
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 max-w-6xl mx-auto">
            <DiagnosticsView />
          </div>
        </div>
      ) : (

      <div className="flex-1 overflow-y-auto">
      <div className="grid grid-cols-12 gap-4 p-4 max-w-[1800px] mx-auto">
        {/* ─ Column 1: current thought + stream ─────────────────────── */}
        <section className="col-span-12 lg:col-span-5 space-y-4">
          <Panel title="CURRENT THOUGHT" accent>
            {primaryThought ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <DomainPill domain={primaryThought.domain} />
                  <span className="text-[9px] tracking-widest text-gray-500">
                    {primaryThought.kind.toUpperCase()} · {ageString(primaryThought.created_at)}
                  </span>
                </div>
                <p className="text-[15px] leading-relaxed text-jarvis-blue/95">
                  {primaryThought.summary || primaryThought.content}
                </p>
                {primaryThought.sources && primaryThought.sources.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {primaryThought.sources.map((s) => (
                      <span key={s} className="text-[8px] px-1.5 py-0.5 bg-jarvis-blue/5 border border-jarvis-blue/20 tracking-wider text-gray-400">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[13px] text-gray-500">Standing by. Nothing surfaced yet.</p>
            )}
            {greeting?.greeting && (
              <div className="mt-4 pt-3 border-t border-jarvis-blue/10">
                <div className="text-[9px] tracking-widest text-gray-500 mb-1">GREETING POOL</div>
                <div className="text-[11px] text-gray-300">{greeting.greeting}</div>
                {greeting.thought && <div className="text-[11px] text-jarvis-blue/70 mt-0.5">{greeting.thought}</div>}
              </div>
            )}
          </Panel>

          <Panel title="STREAM OF CONSCIOUSNESS">
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {thoughts.slice(1).map((t) => (
                <div key={t.id} className="border-l border-jarvis-blue/15 pl-3 py-1">
                  <div className="flex items-center gap-2">
                    <DomainPill domain={t.domain} small />
                    <span className="text-[9px] tracking-widest text-gray-500">
                      {t.kind} · {ageString(t.created_at)}
                      {t.priority != null ? ` · p${t.priority}` : ''}
                    </span>
                  </div>
                  <p className="text-[12px] text-gray-300 leading-snug mt-0.5">
                    {t.summary || t.content}
                  </p>
                </div>
              ))}
              {thoughts.length <= 1 && (
                <p className="text-[11px] text-gray-600">Stream is quiet.</p>
              )}
            </div>
          </Panel>
        </section>

        {/* ─ Column 2: world state + vitals ─────────────────────────── */}
        <section className="col-span-12 lg:col-span-4 space-y-4">
          <Panel title="VITAL SIGNS">
            <div className="grid grid-cols-3 gap-3">
              <Vital label="BPM" value={health.hr ?? '--'} />
              <Vital label="STEPS" value={health.steps != null ? formatK(health.steps) : '--'} />
              <Vital label="SLEEP" value={health.sleep != null ? health.sleep.toFixed(1) : '--'} />
            </div>
          </Panel>

          <Panel title="HORIZON">
            {milestones.length > 0 ? (
              <div className="space-y-2">
                {milestones.slice(0, 5).map((m) => (
                  <div key={m.id} className="flex items-baseline justify-between border-b border-jarvis-blue/10 pb-1.5">
                    <div>
                      <div className="text-[12px] text-gray-200">{m.title}</div>
                      <div className="text-[9px] tracking-wider text-gray-500 mt-0.5">{m.category.toUpperCase()}</div>
                    </div>
                    <div className={`text-[11px] font-bold ${m.days_until != null && m.days_until < 30 ? 'text-jarvis-blue' : 'text-gray-500'}`}>
                      {m.days_until != null ? `${m.days_until}d` : '—'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-gray-600">No milestones in range.</p>
            )}
          </Panel>

          <ApiUsagePanel data={usage} />
        </section>

        {/* ─ Column 3: action queue ─────────────────────────────────── */}
        <section className="col-span-12 lg:col-span-3 space-y-4">
          <Panel title={`ACTION QUEUE (${pending.length})`}>
            {pending.length > 0 ? (
              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                {pending.slice(0, 8).map((t) => (
                  <div key={t.id} className="bg-jarvis-blue/5 border border-jarvis-blue/15 p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <DomainPill domain={t.domain} small />
                      <span className="text-[8px] tracking-widest text-gray-500 ml-auto">{t.action_type?.toUpperCase() ?? '—'}</span>
                    </div>
                    <p className="text-[11px] text-gray-300 leading-snug">{t.summary || t.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-gray-600">No pending actions.</p>
            )}
          </Panel>

          <Panel title={`INVESTIGATIONS (${investigations.length})`}>
            {investigations.length > 0 ? (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                {investigations.slice(0, 6).map((inv) => {
                  const open = !!expandedInv[inv.id];
                  const statusColor =
                    inv.status === 'converged'
                      ? 'text-green-400'
                      : inv.status === 'active'
                      ? 'text-jarvis-blue'
                      : 'text-gray-500';
                  return (
                    <div key={inv.id} className="border border-jarvis-blue/15 bg-black/30 p-2">
                      <button
                        className="w-full text-left flex items-start gap-2"
                        onClick={() =>
                          setExpandedInv((x) => ({ ...x, [inv.id]: !x[inv.id] }))
                        }
                      >
                        <span className="text-[9px] text-gray-500 mt-0.5">{open ? '▼' : '▶'}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <DomainPill domain={inv.domain} small />
                            <span className={`text-[8px] tracking-widest ${statusColor}`}>
                              {inv.status.toUpperCase()}
                            </span>
                            <span className="text-[8px] text-gray-500 ml-auto">
                              STEP {inv.steps.length}/4
                            </span>
                          </div>
                          {/* Title (Haiku-summarized) preferred; falls back to a
                              short slice of the seed question. */}
                          <p className="text-[12px] text-jarvis-blue/90 mt-1 leading-snug line-clamp-1 font-semibold">
                            {inv.title?.trim() || inv.seed_question.slice(0, 60)}
                          </p>
                        </div>
                      </button>
                      {open && (
                        <div className="mt-2 pl-4 border-l border-jarvis-blue/20 space-y-2">
                          {/* Full seed question, no clamp. */}
                          <div className="text-[10px]">
                            <div className="text-[9px] tracking-widest text-gray-500 mb-0.5">
                              SEED QUESTION
                            </div>
                            <div className="text-gray-300 whitespace-pre-wrap break-words">
                              {inv.seed_question}
                            </div>
                          </div>
                          {/* Convergence (the answer) when present. */}
                          {inv.converged_finding && (
                            <div className="text-[10px]">
                              <div className="text-[9px] tracking-widest text-green-400 mb-0.5">
                                CONVERGED FINDING
                              </div>
                              <div className="text-gray-200 whitespace-pre-wrap break-words">
                                {inv.converged_finding}
                              </div>
                            </div>
                          )}
                          {/* Per-step trace, full text. */}
                          {inv.steps.map((s, i) => (
                            <div key={s.sim_id || i} className="text-[10px]">
                              <div className="text-gray-400 whitespace-pre-wrap break-words">
                                Step {i + 1}: {s.question}
                              </div>
                              {s.findings.map((f, j) => (
                                <div
                                  key={j}
                                  className="text-gray-500 pl-2 whitespace-pre-wrap break-words"
                                >
                                  → {f}
                                </div>
                              ))}
                            </div>
                          ))}
                          {inv.converged_finding && (
                            <div className="text-[10px] text-green-400 mt-1">
                              ✓ {inv.converged_finding}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[11px] text-gray-600">No active investigations.</p>
            )}
          </Panel>
        </section>
      </div>
      </div>
      )}
    </div>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────────

function Panel({ title, accent, children }: { title: string; accent?: boolean; children: React.ReactNode }) {
  return (
    <div className={`relative bg-black/40 ${accent ? 'border-jarvis-blue/35' : 'border-jarvis-blue/15'} border p-4`}
         style={{ clipPath: 'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)' }}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-1 h-3 ${accent ? 'bg-jarvis-blue' : 'bg-jarvis-blue/40'}`} />
        <h2 className="text-[10px] tracking-[0.3em] text-gray-400">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function DomainPill({ domain, small }: { domain: string; small?: boolean }) {
  const color = DOMAIN_COLOR[domain] || '#888';
  return (
    <span
      className={`${small ? 'text-[8px] px-1.5 py-0.5' : 'text-[9px] px-2 py-0.5'} tracking-widest font-bold border`}
      style={{ color, borderColor: color + '55', background: color + '15' }}
    >
      {domain.replace('_', ' ').toUpperCase()}
    </span>
  );
}

function Vital({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-[8px] tracking-widest text-gray-500 mb-1">{label}</div>
      <div className="text-2xl text-white font-light">{value}</div>
    </div>
  );
}

function formatK(n: number): string {
  // Below 10k: show the literal count so 11,059 doesn't compress to
  // a misleading "11.1k" when the panel has the room. Above 10k:
  // compact form with one decimal.
  if (n < 1000) return String(n);
  if (n < 10000) return n.toLocaleString();
  return `${(n / 1000).toFixed(1)}k`;
}

// ─── Sprint 14-A A.4 — API USAGE panel ─────────────────────────────────

interface ApiUsageServiceBucket {
  calls: number;
  tokens: number;
  cost_usd: number;
  top_callers: { caller: string; calls: number }[];
}

interface ApiUsageSummary {
  window_seconds: number;
  since?: string;
  total_cost_usd: number;
  total_calls: number;
  claude_code_max_dispatch_count: number;
  by_service: Record<string, ApiUsageServiceBucket>;
}

// Per Sprint 14-A spec: Claude Code Max dispatches >3 → amber, >6 → red.
// Today's Max-quota burn came from ~10 autonomous dispatches in a few hours
// and was completely invisible. The threshold is deliberately conservative.
function dispatchSeverityColor(count: number): string {
  if (count > 6) return 'text-red-500';
  if (count > 3) return 'text-amber-400';
  return 'text-jarvis-blue';
}

function formatUSD(n: number): string {
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 10) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(0)}`;
}

function ApiUsagePanel({ data }: { data: ApiUsageSummary | null }) {
  if (!data) {
    return (
      <Panel title="API USAGE">
        <p className="text-[11px] text-gray-600">Loading...</p>
      </Panel>
    );
  }

  const services = Object.entries(data.by_service || {})
    .sort((a, b) => {
      // Cost-bearing services first (descending cost), then by call count.
      const dc = (b[1].cost_usd || 0) - (a[1].cost_usd || 0);
      if (Math.abs(dc) > 1e-6) return dc;
      return (b[1].calls || 0) - (a[1].calls || 0);
    })
    .slice(0, 8);

  const dispatchColor = dispatchSeverityColor(data.claude_code_max_dispatch_count);

  return (
    <Panel title="API USAGE">
      {/* Headline row: spend, total calls, the Max-dispatch alarm. */}
      <div className="grid grid-cols-3 gap-3 mb-3 pb-3 border-b border-jarvis-blue/15">
        <Vital label="24H COST" value={formatUSD(data.total_cost_usd)} />
        <Vital label="CALLS" value={formatK(data.total_calls)} />
        <div>
          <div className="text-[8px] tracking-widest text-gray-500 mb-1">CC MAX</div>
          <div className={`text-2xl font-light ${dispatchColor}`}>
            {data.claude_code_max_dispatch_count}
          </div>
        </div>
      </div>

      {/* Per-service breakdown. */}
      {services.length > 0 ? (
        <div className="space-y-1.5">
          {services.map(([name, b]) => (
            <div key={name} className="flex items-center gap-2 text-[11px]">
              <span className="text-gray-300 flex-1 truncate" title={name}>
                {name}
              </span>
              <span className="text-gray-500 text-[10px] tabular-nums w-14 text-right">
                {formatK(b.calls)}
              </span>
              <span
                className={`text-[10px] tabular-nums w-14 text-right ${
                  b.cost_usd > 0 ? 'text-jarvis-blue' : 'text-gray-600'
                }`}
              >
                {b.cost_usd > 0 ? formatUSD(b.cost_usd) : '—'}
              </span>
            </div>
          ))}
          {/* Top caller across the highest-cost service is the forensic
              clue when usage spikes — surface it inline. */}
          {(() => {
            const lead = services[0];
            const top = lead?.[1]?.top_callers?.[0];
            if (!top) return null;
            return (
              <div className="mt-3 pt-2 border-t border-jarvis-blue/10 text-[10px] text-gray-500">
                top caller {' '}
                <span className="text-gray-300">{top.caller}</span>
                {' '}({top.calls}×)
              </div>
            );
          })()}
        </div>
      ) : (
        <p className="text-[11px] text-gray-600">No external API calls in the last 24h.</p>
      )}
    </Panel>
  );
}
